import React, { useState } from "react";
import { TextField, Button, Container, Typography, Box, Snackbar, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { registerUser, loginUser } from "../authService"; // Import auth services
import Header from "../Homepage/Components/Header";
import Video from "./Login.mp4"; // Path to your video file

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState(""); // To handle the name input for registration
  const [isLogin, setIsLogin] = useState(true); // Toggle between login and signup
  const [error, setError] = useState("");  // Error state
  const [openError, setOpenError] = useState(false); // Snackbar visibility state
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isLogin) {
        const user = await loginUser(email, password);
        if (user) navigate("/");
      } else {
        const user = await registerUser(email, password, name);
        if (user) navigate("/");
      }
    } catch (error) {
      const friendlyMessage = getFriendlyErrorMessage(error); // Map to friendly message
      setError(friendlyMessage); // Set the user-friendly error message
      setOpenError(true); // Trigger error display
    }
  };

  const handleForgotPassword = () => {
    // Navigate to the Forgot Password page
    navigate("/ForgotPasswordPage");
  };

  const getFriendlyErrorMessage = (error) => {
    if (!error) return "An unknown error occurred. Please try again.";
    const message = (error.message || "An unexpected error occurred.").toLowerCase();
    switch (true) {
      case message.includes("auth/invalid-email"):
        return "The email address is not valid. Please check and try again.";
      case message.includes("auth/user-disabled"):
        return "This account has been disabled. Please contact support.";
      case message.includes("auth/user-not-found"):
        return "No user found with this email address. Please check and try again.";
      case message.includes("auth/wrong-password"):
        return "The password you entered is incorrect. Please try again.";
      case message.includes("auth/email-already-in-use"):
        return "This email address is already in use. Please use a different email.";
      case message.includes("auth/weak-password"):
        return "The password is too weak. Please use a stronger password.";
      case message.includes("auth/network-request-failed"):
        return "Network error. Please check your connection and try again.";
      case message.includes("auth/too-many-requests"):
        return "You have tried too many times. Please wait a moment and try again.";
      case message.includes("auth/invalid-credential"):
        return "Your credentials are invalid. Please check your email or password and try again.";
      default:
        return "An unexpected error occurred. Please try again later.";
    }
  };

  return (
    <div>
      <Header />
      <div style={{ position: "relative", height: "100vh" }}>
        {/* Video Background */}
        <video
          src={Video}
          type="video/mp4"
          autoPlay
          loop
          muted
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: -1,
          }}
        />
        {/* Overlay with opacity */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 0,
          }}
        ></div>

        {/* Main Content */}
        <div
          style={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            backgroundColor: "transparent",
          }}
        >
          <Container
            maxWidth="xs"
            sx={{
              padding: 4,
              backgroundColor: "#fff",
              borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  fontFamily: "'Work Sans', sans-serif",
                  color: "#2C3E50",
                  textAlign: "center",
                }}
              >
                {isLogin ? "Welcome Back!" : "Create an Account"}
              </Typography>
              {!isLogin && (
                <TextField
                  label="Name"
                  variant="outlined"
                  fullWidth
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  sx={{ backgroundColor: "#f9f9f9" }}
                />
              )}
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{ backgroundColor: "#f9f9f9" }}
              />
              <TextField
                label="Password"
                variant="outlined"
                type="password"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                sx={{ backgroundColor: "#f9f9f9" }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: "#007bff",
                  color: "white",
                  fontWeight: "bold",
                  padding: "12px",
                  borderRadius: "8px",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#0056b3",
                  },
                }}
              >
                {isLogin ? "Log In" : "Sign Up"}
              </Button>
              <Button
                onClick={() => setIsLogin(!isLogin)}
                sx={{
                  color: "#007bff",
                  fontWeight: "bold",
                  textTransform: "none",
                  textAlign: "center",
                }}
              >
                {isLogin
                  ? "Don't have an account? Create one"
                  : "Already have an account? Log in"}
              </Button>

              {/* Forgot Password Link */}
              {isLogin && (
                <Button
                  onClick={handleForgotPassword}
                  sx={{
                    color: "#007bff",
                    fontWeight: "bold",
                    textTransform: "none",
                    textAlign: "center",
                    marginTop: "8px",
                  }}
                >
                  Forgot Password?
                </Button>
              )}
            </Box>
          </Container>
        </div>
      </div>

      {/* Error Snackbar moved below the login card */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
        <Snackbar
          open={openError}
          autoHideDuration={6000}
          onClose={() => setOpenError(false)}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
        >
          <Alert onClose={() => setOpenError(false)} severity="error" sx={{ width: "100%", fontSize: "16px", borderRadius: 2 }}>
            {error}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default LoginPage;
